import clsx from "clsx";
import type { SupportTicket } from "@/lib/support";
import { STATUS_LABEL, URGENCY_LABEL } from "@/pages/support/constants";
import { ReporterReplyBadge, StatusBadge } from "@/pages/support/SupportBadges";
import { fmtDate, fmtRelative, ticketNeedsReporterAction } from "@/pages/support/utils";

export function UserTicketList({
  items,
  selectedId,
  onSelect,
}: {
  items: SupportTicket[];
  selectedId: number | null;
  onSelect: (t: SupportTicket) => void;
}) {
  if (!items.length) {
    return (
      <p className="py-8 text-center text-sm text-label-tertiary">
        Keine Tickets in dieser Ansicht.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {items.map((ticket) => {
        const status = String(ticket.status || "open").toLowerCase();
        const urgency = String(ticket.urgency || "normal").toLowerCase();
        const updated = ticket.last_reply_at || ticket.updated_at || ticket.created_at;
        const needsAction = ticketNeedsReporterAction(ticket);

        return (
          <button
            key={ticket.id}
            type="button"
            onClick={() => onSelect(ticket)}
            className={clsx(
              "support-list-item w-full rounded-ios border px-3 py-2.5 text-left transition",
              selectedId === ticket.id
                ? "border-accent bg-accent-soft/30 ring-1 ring-accent/40"
                : needsAction
                  ? "border-l-2 border-l-status-orange border-separator/60 bg-card hover:bg-fill-tertiary"
                  : "border-separator/60 bg-card hover:bg-fill-tertiary",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-label-secondary">#{ticket.id}</span>
                  {needsAction && status !== "waiting" && status !== "resolved" ? (
                    <ReporterReplyBadge />
                  ) : null}
                  {status === "waiting" && needsAction ? (
                    <span className="rounded-full bg-status-orange/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-status-orange">
                      Rückfrage
                    </span>
                  ) : null}
                  {status === "resolved" ? (
                    <span className="rounded-full bg-status-green/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-status-green">
                      Bestätigen
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 truncate text-sm font-medium text-label">{ticket.title || "—"}</div>
              </div>
              <StatusBadge
                status={status}
                label={STATUS_LABEL[status] || status}
              />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-xs text-label-secondary">
              <span>{ticket.program || "—"}</span>
              <span>·</span>
              <span title={fmtDate(updated)}>{fmtRelative(updated)}</span>
              <span>·</span>
              <span>{URGENCY_LABEL[urgency] || urgency}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
